import React, { useState, useEffect } from "react";
import Dashboard from "./components/Dashboard";
import OrderDetail from "./components/OrderDetail";
import Settings from "./components/Settings";
import Roster from "./components/Roster";
import RunSheets from "./components/RunSheets";
import OCRMonitor from "./components/OCRMonitor";
import Timesheets from "./components/Timesheets";
import Trucks from "./components/Trucks";
import API from "./services/api";
import { Truck, Settings as SettingsIcon, BarChart3, AlertCircle, Calendar, ClipboardList, Activity, Clock, LogOut } from "lucide-react";

import Login from "./components/Login";
import DriverApp from "./components/DriverApp";
import ChangePassword from "./components/ChangePassword";

// Who gets the office console. Everything else - a driver, an offsider, a
// role a later backend invents, or a session with no role stored at all -
// gets the crew app. The default is DENY on purpose: "offsider" used to fall
// through to the full admin dashboard, Settings and the crew API included.
const CONSOLE_ROLES = ["admin", "it", "dispatcher"];

// Who may change things. Everyone else with a console login can look but not
// touch - the backend enforces the same list, this only keeps the UI honest
// about it. IT is deliberately its own role rather than an alias for admin,
// so the audit trail says which of the two made a change.
const EDIT_ROLES = ["admin", "it"];

// The tab bar is data so the role gate lives in one place. `admin: true` is
// office-only work: crew records, logins, the pay fortnight, and the trucks'
// daily checks (a driver reads only their own, in the app).
const TABS = [
  { key: "dashboard", label: "Dashboard", Icon: BarChart3, also: ["detail"] },
  { key: "run_sheets", label: "Run Sheets", Icon: ClipboardList },
  { key: "ocr_monitor", label: "OCR Monitor", Icon: Activity },
  { key: "roster", label: "Daily Hours", Icon: Calendar },
  { key: "trucks", label: "Trucks", Icon: Truck, admin: true },
  { key: "timesheets", label: "Timesheets", Icon: Clock, admin: true },
  { key: "settings", label: "Settings", Icon: SettingsIcon, admin: true },
];

export default function App() {
  // A stored token is only a hint that we may be signed in - it is verified
  // against the API below, because it can be expired or revoked.
  const [isAuth, setIsAuth] = useState(!!localStorage.getItem("dav_token"));
  const [authChecked, setAuthChecked] = useState(false);
  // No fallback to "admin": an unknown role must not open the console.
  const [role, setRole] = useState(localStorage.getItem("dav_role") || "");
  const [username, setUsername] = useState(localStorage.getItem("dav_username") || "");
  // True while this account is still on a password an admin handed out. The
  // server decides - the stored value only avoids a flash of the wrong screen
  // before /auth/verify answers.
  const [mustChange, setMustChange] = useState(localStorage.getItem("dav_must_change") === "1");
  // Drivers land straight in the driver app; the operator can open it
  // from the dashboard to see exactly what a driver sees.
  const [driverView, setDriverView] = useState(
    new URLSearchParams(window.location.search).get("app") === "driver");
  const [view, setView] = useState("dashboard");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [health, setHealth] = useState(null);
  const [doclingHealth, setDoclingHealth] = useState(null);

  useEffect(() => {
    // Confirm the stored token is still good before showing the dashboard;
    // a 401 anywhere in the app clears it and brings the login screen back.
    let cancelled = false;
    const token = localStorage.getItem("dav_token");
    if (!token) {
      setIsAuth(false);
      setAuthChecked(true);
    } else {
      API.get("/auth/verify")
        .then((r) => {
          if (cancelled) return;
          setIsAuth(true);
          if (r.data?.role) {
            setRole(r.data.role);
            localStorage.setItem("dav_role", r.data.role);
          }
          if (r.data?.user) {
            setUsername(r.data.user);
            localStorage.setItem("dav_username", r.data.user);
          }
          const owed = !!r.data?.must_change_password;
          setMustChange(owed);
          localStorage.setItem("dav_must_change", owed ? "1" : "0");
        })
        .catch((err) => {
          if (cancelled) return;
          // Only a real rejection signs the operator out - a network blip or
          // a backend restart must not wipe their session mid-shift.
          if (err.response && err.response.status === 401) {
            localStorage.removeItem("dav_token");
            setIsAuth(false);
          }
        })
        .finally(() => { if (!cancelled) setAuthChecked(true); });
    }
    const onUnauthorized = () => {
      localStorage.removeItem("dav_token");
      setIsAuth(false);
    };
    window.addEventListener("dav:unauthorized", onUnauthorized);
    return () => {
      cancelled = true;
      window.removeEventListener("dav:unauthorized", onUnauthorized);
    };
  }, []);

  useEffect(() => {
    if (!isAuth) return undefined;
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [isAuth]);

  // Clearing the token is the only way out of a session today - it otherwise
  // survives until a 401 - and a shared cab tablet needs one.
  function logout() {
    // dav_shift_state and dav_my_hours are the PREVIOUS driver's cached shift,
    // pre-check and hours - on a shared cab phone the next driver must not
    // inherit them for the seconds before /time/current answers.
    ["dav_token", "dav_role", "dav_username", "dav_driver_id", "dav_must_change",
     "dav_shift_state", "dav_my_hours"]
      .forEach((k) => localStorage.removeItem(k));
    delete API.defaults.headers.common.Authorization;
    setRole("");
    setUsername("");
    setMustChange(false);
    setDriverView(false);
    setIsAuth(false);
  }

  // Hold the frame while the stored token is being verified, so a valid
  // session never flashes the login screen on load.
  if (!authChecked && isAuth) {
    return <div className="min-h-screen bg-slate-950" />;
  }
  if (!isAuth) {
    return <Login setAuth={(v) => {
      setIsAuth(v);
      setAuthChecked(true);
      setRole(localStorage.getItem("dav_role") || "");
      setUsername(localStorage.getItem("dav_username") || "");
      setMustChange(localStorage.getItem("dav_must_change") === "1");
    }} />;
  }

  // Before anything else, including the driver app: an account still on the
  // password it was handed cannot be allowed to do work under that identity.
  if (mustChange) {
    return <ChangePassword
      username={username}
      onLogout={logout}
      onDone={() => {
        setMustChange(false);
        localStorage.setItem("dav_must_change", "0");
      }}
    />;
  }

  const isAdmin = EDIT_ROLES.includes(role);
  const isCrew = !CONSOLE_ROLES.includes(role);

  if (isCrew || driverView) {
    return <DriverApp onExit={isCrew ? null : () => setDriverView(false)} onLogout={logout} />;
  }

  // A tab this role cannot see must not render even if `view` still points at
  // it. Views with no tab of their own (the order detail) are reachable by
  // any console role.
  const canSee = (key) => {
    const tab = TABS.find((t) => t.key === key);
    return !tab || !tab.admin || isAdmin;
  };
  const tabs = TABS.filter((t) => !t.admin || isAdmin);

  function checkHealth() {
    API.get("/health")
      .then(r => {
        setHealth(r.data);
        setDoclingHealth(r.data.docling || { status: "error" });
      })
      .catch(() => {
        setHealth({ status: "error" });
        setDoclingHealth({ status: "error" });
      });
  }

  const views = {
    dashboard: <Dashboard onSelectOrder={(o) => { setSelectedOrder(o); setView("detail"); }} />,
    detail: <OrderDetail order={selectedOrder} onBack={() => setView("dashboard")} />,
    run_sheets: <RunSheets />,
    ocr_monitor: <OCRMonitor />,
    roster: <Roster />,
    trucks: <Trucks />,
    timesheets: <Timesheets />,
    settings: <Settings />,
  };

  return (
    <div className="min-h-screen text-slate-100 font-sans selection:bg-blue-500 selection:text-white">
      {/* Premium Header */}
      <nav className="bg-slate-950/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-800/80 px-6 py-4 flex items-center justify-between shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600/10 p-2 rounded-xl border border-blue-500/20 text-blue-400 overflow-hidden flex items-center justify-center w-10 h-10">
            <img 
              src="/logo.png" 
              alt="DAV" 
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Truck size={24} style={{ display: 'none' }} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-white flex items-center gap-1.5">
              DAV Transport <span className="text-[10px] uppercase tracking-widest text-blue-500 font-bold bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">Control</span>
            </h1>
            <p className="text-[10px] text-slate-400">Delivery Management System</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex bg-slate-900/60 p-1 rounded-xl border border-slate-800 gap-1 flex-wrap">
          {tabs.map(({ key, label, Icon, also }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all ${
                view === key || (also || []).includes(view)
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/10"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {/* System Status */}
        <div className="flex items-center gap-4 bg-slate-900/60 p-2 px-4 rounded-xl border border-slate-800">
          {/* Backend Status */}
          <div className="flex flex-col items-end">
            <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">Backend Server</span>
            {health?.status === "ok" ? (
              <div className="flex items-center gap-1.5 text-[11px] text-green-400 font-medium">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
                <span>Online</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-[11px] text-red-400 font-medium">
                <AlertCircle size={10} className="animate-pulse" />
                <span>Offline</span>
              </div>
            )}
          </div>
          
          <div className="h-6 w-px bg-slate-800"></div>
          
          {/* Docling Status */}
          <div className="flex flex-col items-start">
            <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">Docling AI</span>
            {doclingHealth?.status === "ok" ? (
              <div className="flex items-center gap-1.5 text-[11px] text-green-400 font-medium">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
                <span>Online</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-[11px] text-red-400 font-medium">
                <AlertCircle size={10} className="animate-pulse" />
                <span>Offline</span>
              </div>
            )}
          </div>

          <div className="h-6 w-px bg-slate-800"></div>

          {/* Who is signed in, and the way out of the session */}
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end leading-tight">
              <span className="text-[11px] font-semibold text-slate-200">{username || "Operator"}</span>
              <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">{role || "unknown"}</span>
            </div>
            <button
              onClick={logout}
              title="Sign out of this device"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-slate-400 hover:text-white bg-slate-900/60 border border-slate-800 hover:border-slate-600 transition-all"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Container */}
      <main className="max-w-[1680px] mx-auto px-5 py-6 md:px-8">
        <div className="transition-all duration-300">
          {canSee(view) ? views[view] : views.dashboard}
        </div>
      </main>
    </div>
  );
}
